import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SquareStack, ArrowRight } from "lucide-react";
import { usePageMeta } from "../../hooks/usePageMeta";
import officePartitioning1 from "../../assets/images/OfficePartitioning1.webp";
import officePartitioning2 from "../../assets/images/OfficePartitioning2.webp";
import officePartitioning3 from "../../assets/images/OfficePartitioning3.webp";

const OfficePartitioning = () => {
  usePageMeta(
    "Office Partitioning | Sandhurst Interiors",
    "Office and warehouse partitioning specialists. Soundproof, glazed, steel and timber partitions. Executive offices, meeting rooms and collaborative spaces across the South East."
  );

  return (
    <div className="min-h-screen">
      <section className="section-padding bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Office & Warehouse Partitioning
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialise in all types of office and warehouse partitioning—
              creating privacy, reducing noise and defining your space with
              soundproof, glazed and modular systems.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700"
          >
            <p>
              Sandhurst Interiors has delivered commercial fit-outs and
              refurbishment across the South East for over 30 years. Partitioning
              is one of our core specialisms—we install soundproof partitions,
              glazed screens, frameless glass dividers and modular office
              dividers that suit executive offices, meeting rooms, quiet zones
              and collaborative spaces.
            </p>
            <p>
              Whether you need to divide an open plan, create private offices or
              define breakout areas, we provide custom solutions that match your
              layout and acoustic requirements. All our contractors are
              time-served professionals committed to precision finishes and
              reliable delivery.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Types of Partitioning We Install
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Soundproof partitions", desc: "Reduce noise between areas for focus and privacy." },
              { title: "Glazed screens", desc: "Let light through while defining zones and meeting rooms." },
              { title: "Frameless glass dividers", desc: "Modern, clean look with maximum visibility." },
              { title: "Modular office dividers", desc: "Flexible layouts that can adapt as you grow." },
              { title: "Steel and timber partitioning", desc: "Robust options for offices and industrial spaces." },
              { title: "Warehouse partitioning", desc: "Define storage, office and operational areas." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="card"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3 text-gray-700">
                <SquareStack className="w-6 h-6 text-primary-600 flex-shrink-0" />
                <span className="font-medium">Part of a full fit-out</span>
              </div>
              <p className="text-gray-700">
                Partitioning often sits alongside suspended ceilings, flooring,
                electrical and decorating as part of a full office fit-out or
                refurbishment. We handle the whole project—design, space
                planning and delivery—so you have one team and one point of
                contact from start to finish.
              </p>
              <Link
                to="/services"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>View All Services</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={officePartitioning1}
                  alt="Office partitioning installation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={officePartitioning2}
                  alt="Glazed office partitions"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 aspect-video rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={officePartitioning3}
                  alt="Office partitioning project"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need partitioning for your office or warehouse?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Get a quote for partitioning as a standalone project or as part of
              a full fit-out.
            </p>
            <Link
              to="/contact#contact-form"
              onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center space-x-2"
            >
              <span>Get in Touch</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OfficePartitioning;
